import { useState, useCallback, useEffect } from "react";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string;
}

export default function ShareModal({ isOpen, onClose, title, url }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [wechatCopied, setWechatCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setWechatCopied(false);
    }
  }, [isOpen]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  const handleWechatCopy = useCallback(() => {
    navigator.clipboard.writeText(`${title}\n${url}`).then(() => {
      setWechatCopied(true);
      setTimeout(() => setWechatCopied(false), 2000);
    });
  }, [title, url]);

  const handleWeiboShare = useCallback(() => {
    const shareUrl = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&pic=`;
    window.open(shareUrl, "_blank", "width=600,height=400");
  }, [url, title]);

  const handleTwitterShare = useCallback(() => {
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, "_blank", "width=600,height=400");
  }, [url, title]);

  const handleQQShare = useCallback(() => {
    const shareUrl = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;
    window.open(shareUrl, "_blank", "width=600,height=400");
  }, [url, title]);

  if (!isOpen) return null;

  const shareItems = [
    {
      id: "wechat",
      name: "微信",
      icon: "ri-wechat-fill",
      color: "bg-emerald-500",
      colorLight: "bg-emerald-50",
      textColor: "text-emerald-700",
      onClick: handleWechatCopy,
      copied: wechatCopied,
      copiedText: "已复制",
    },
    {
      id: "weibo",
      name: "微博",
      icon: "ri-weibo-fill",
      color: "bg-red-500",
      colorLight: "bg-red-50",
      textColor: "text-red-700",
      onClick: handleWeiboShare,
    },
    {
      id: "twitter",
      name: "Twitter",
      icon: "ri-twitter-x-fill",
      color: "bg-foreground-900",
      colorLight: "bg-foreground-50",
      textColor: "text-foreground-800",
      onClick: handleTwitterShare,
    },
    {
      id: "qq",
      name: "QQ",
      icon: "ri-qq-fill",
      color: "bg-sky-500",
      colorLight: "bg-sky-50",
      textColor: "text-sky-700",
      onClick: handleQQShare,
    },
    {
      id: "link",
      name: "复制链接",
      icon: "ri-link",
      color: "bg-secondary-500",
      colorLight: "bg-secondary-50",
      textColor: "text-secondary-700",
      onClick: handleCopyLink,
      copied,
      copiedText: "已复制",
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-background-50 rounded-2xl p-6 md:p-8 max-w-sm w-[90%] mx-auto shadow-lg animate-modal-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line"></i>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
            <i className="ri-share-forward-fill text-xl text-primary-500"></i>
          </span>
          <h3 className="text-lg font-heading font-bold text-foreground-950">分享这篇文章</h3>
          <p className="text-xs text-foreground-400 mt-1">选择分享平台</p>
        </div>

        {/* Share grid */}
        <div className="grid grid-cols-5 gap-3">
          {shareItems.map((item) => (
            <button
              key={item.id}
              onClick={item.onClick}
              className="flex flex-col items-center gap-2 cursor-pointer group whitespace-nowrap"
              title={item.name}
            >
              <div
                className={`w-12 h-12 flex items-center justify-center rounded-xl ${item.colorLight} ${item.textColor} group-hover:scale-110 transition-transform duration-200`}
              >
                <i className={`${item.icon} text-xl`}></i>
              </div>
              <span className="text-xs text-foreground-500 group-hover:text-foreground-800 transition-colors">
                {"copied" in item && item.copied ? item.copiedText : item.name}
              </span>
            </button>
          ))}
        </div>

        {/* URL bar */}
        <div className="mt-6 flex items-center gap-2 p-3 rounded-lg bg-background-100 border border-background-200/70">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-foreground-300">
            <i className="ri-link text-sm"></i>
          </span>
          <span className="text-xs text-foreground-500 truncate flex-1">{url}</span>
          <button
            onClick={handleCopyLink}
            className="px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            {copied ? "已复制" : "复制"}
          </button>
        </div>
      </div>
    </div>
  );
}